import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Mail, Search, Download, Filter } from "lucide-react";
import { PageHeader } from "@/components/admin/Form";

const PARTNER_STATUSES = [
    { value: "new", label: "New" },
    { value: "contacted", label: "Contacted" },
    { value: "in_discussion", label: "In Discussion" },
    { value: "closed", label: "Closed" },
];
const CONTACT_STATUSES = [
    { value: "new", label: "New" },
    { value: "read", label: "Read" },
    { value: "replied", label: "Replied" },
    { value: "closed", label: "Closed" },
];
const CONTACT_REASONS = ["Feedback", "Official", "Supply Vendor", "Real Estate", "Franchise", "Media & Collab", "Events & Catering", "Other"];
const REASON_COLORS = {
    "Feedback": "#c8a96e", "Official": "#8a6b3d", "Supply Vendor": "#a45a2a",
    "Real Estate": "#3d6b8a", "Franchise": "#7a3d8a", "Media & Collab": "#8a3d5e",
    "Events & Catering": "#6b8a3d", "Other": "#5a5a5a",
};
const STATUS_COLORS = {
    new: "bg-gold/20 text-gold border-gold/40",
    contacted: "bg-blue-100 text-blue-800 border-blue-300",
    in_discussion: "bg-orange-100 text-orange-800 border-orange-300",
    read: "bg-blue-100 text-blue-800 border-blue-300",
    replied: "bg-green-100 text-green-800 border-green-300",
    closed: "bg-coffee/15 text-coffee/70 border-coffee/30",
};

export default function Messages() {
    const [tab, setTab] = useState("contacts");
    const [data, setData] = useState({ contacts: [], partners: [], careers: [], newsletter: [] });
    const [loading, setLoading] = useState(true);

    // Partner CRM filters
    const [partnerSearch, setPartnerSearch] = useState("");
    const [partnerStatusFilter, setPartnerStatusFilter] = useState("all");
    const [partnerTypeFilter, setPartnerTypeFilter] = useState("all");

    // Contact CRM filters
    const [contactReasonFilter, setContactReasonFilter] = useState("All");
    const [expandedContact, setExpandedContact] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/messages");
            setData(data);
        } catch { toast.error("Failed to load messages"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const remove = async (kind, id) => {
        if (!window.confirm("Delete?")) return;
        try {
            await api.delete(`/admin/messages/${kind}/${id}`);
            await load();
        } catch { toast.error("Delete failed"); }
    };

    const updatePartnerStatus = async (id, status) => {
        try {
            await api.patch(`/admin/messages/partner/${id}`, { status });
            setData((d) => ({ ...d, partners: d.partners.map((p) => p.id === id ? { ...p, status } : p) }));
            toast.success("Status updated");
        } catch { toast.error("Update failed"); }
    };

    const updateContactStatus = async (id, status) => {
        try {
            await api.patch(`/admin/messages/contact/${id}`, { status });
            setData((d) => ({ ...d, contacts: d.contacts.map((m) => m.id === id ? { ...m, status } : m) }));
            toast.success("Status updated");
        } catch { toast.error("Update failed"); }
    };

    const exportContactsCsv = async () => {
        try {
            const res = await api.get("/admin/messages/contact/export.csv", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = "contact_messages.csv";
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch { toast.error("Export failed"); }
    };

    const filteredContacts = (data.contacts || []).filter((m) => {
        if (contactReasonFilter === "All") return true;
        return (m.reason || "") === contactReasonFilter;
    });

    const exportPartnersCsv = async () => {
        try {
            const res = await api.get("/admin/messages/partner/export.csv", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = "partner_inquiries.csv";
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch { toast.error("Export failed"); }
    };

    const filteredPartners = useMemo(() => {
        const q = partnerSearch.trim().toLowerCase();
        return (data.partners || []).filter((p) => {
            const status = (p.status || "new").toLowerCase();
            const interest = (p.interest || "").toLowerCase();
            if (partnerStatusFilter !== "all" && status !== partnerStatusFilter) return false;
            if (partnerTypeFilter !== "all" && interest !== partnerTypeFilter.toLowerCase()) return false;
            if (!q) return true;
            return [p.name, p.email, p.city, p.phone, p.message, p.investment].some((v) => (v || "").toLowerCase().includes(q));
        });
    }, [data.partners, partnerSearch, partnerStatusFilter, partnerTypeFilter]);

    const tabs = [
        { key: "contacts", label: `Contact (${data.contacts.length})` },
        { key: "partners", label: `Partner (${data.partners.length})` },
        { key: "careers", label: `Careers (${(data.careers || []).length})` },
        { key: "newsletter", label: `Newsletter (${data.newsletter.length})` },
    ];

    return (
        <div data-testid="messages-page">
            <PageHeader subtitle="Inbox" title="Messages from your hideout" />

            <div className="flex gap-2 mb-8 flex-wrap">
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`tab-${t.key}`}
                        className={`tracked text-[10px] px-5 py-3 transition-colors ${
                            tab === t.key ? "bg-coffee text-cream" : "bg-white text-coffee border border-coffee/15 hover:border-coffee"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="tracked text-coffee/50 text-[11px]">Loading…</div>
            ) : (
                <div>
                    {/* CONTACTS — Inbox with reason filter + status */}
                    {tab === "contacts" && (
                        <div>
                            <div className="bg-white border border-coffee/10 p-4 mb-4 flex flex-wrap gap-3 items-center" data-testid="contact-toolbar">
                                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 flex-1">
                                    {["All", ...CONTACT_REASONS].map((r) => {
                                        const active = contactReasonFilter === r;
                                        return (
                                            <button
                                                key={r}
                                                onClick={() => setContactReasonFilter(r)}
                                                data-testid={`contact-filter-${r.toLowerCase().replace(/\W+/g, "-")}`}
                                                className={`whitespace-nowrap tracked text-[10px] px-3 py-2 border transition-colors ${
                                                    active ? "bg-coffee text-gold border-coffee" : "bg-cream-light text-coffee border-coffee/15 hover:border-coffee"
                                                }`}
                                            >
                                                {r}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button onClick={exportContactsCsv} className="inline-flex items-center gap-2 tracked text-[10px] px-4 py-2.5 bg-coffee text-cream hover:bg-gold hover:text-coffee transition-colors whitespace-nowrap" data-testid="contact-export-csv">
                                    <Download size={12} /> Export CSV
                                </button>
                            </div>

                            <div className="space-y-3">
                                {filteredContacts.length === 0 ? <Empty /> : filteredContacts.map((m) => {
                                    const status = m.status || "new";
                                    const cls = STATUS_COLORS[status] || STATUS_COLORS.new;
                                    const isOpen = expandedContact === m.id;
                                    const reasonColor = REASON_COLORS[m.reason] || "#5a5a5a";
                                    return (
                                        <div key={m.id} className="bg-white border border-coffee/10" data-testid={`contact-${m.id}`}>
                                            <button onClick={() => setExpandedContact(isOpen ? null : m.id)} className="w-full text-left p-5 flex items-start justify-between gap-4 hover:bg-cream/30 transition-colors">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 flex-wrap mb-1">
                                                        <span className="font-editorial font-bold text-coffee text-lg truncate">{m.name}</span>
                                                        {m.reason && (
                                                            <span className="tracked text-[9px] px-2 py-1" style={{ background: reasonColor, color: "#fff" }}>{m.reason}</span>
                                                        )}
                                                    </div>
                                                    <div className="font-mono text-[10px] text-coffee/50 truncate">{m.subject || "—"}</div>
                                                    <div className="text-coffee/65 text-sm mt-1 truncate">{m.email} {m.phone && `· ${m.phone}`} {m.city && `· ${m.city}`}</div>
                                                </div>
                                                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                                    <select value={status} onClick={(e) => e.stopPropagation()} onChange={(e) => updateContactStatus(m.id, e.target.value)} data-testid={`contact-status-${m.id}`} className={`text-[10px] tracked py-1.5 px-2 border outline-none ${cls}`}>
                                                        {CONTACT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                                    </select>
                                                    <div className="font-mono text-[10px] text-coffee/40">{new Date(m.created_at).toLocaleDateString()}</div>
                                                </div>
                                            </button>
                                            {isOpen && (
                                                <div className="px-5 pb-5 border-t border-coffee/8 pt-4" data-testid={`contact-detail-${m.id}`}>
                                                    <p className="font-body text-coffee/80 whitespace-pre-wrap leading-relaxed mb-4">{m.message}</p>
                                                    {m.source && <div className="text-coffee/55 text-xs mb-3">Source: <span className="text-coffee">{m.source}</span></div>}
                                                    <div className="flex gap-3 flex-wrap">
                                                        <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 tracked text-[10px] px-4 py-2 bg-coffee text-cream hover:bg-gold hover:text-coffee transition-colors">
                                                            <Mail size={12} /> Reply
                                                        </a>
                                                        {status !== "replied" && (
                                                            <button onClick={() => updateContactStatus(m.id, "replied")} className="inline-flex items-center gap-1.5 tracked text-[10px] px-4 py-2 text-coffee hover:bg-coffee hover:text-cream transition-colors" style={{ border: "1px solid rgba(26,15,0,0.25)" }}>
                                                                Mark as Replied
                                                            </button>
                                                        )}
                                                        <button onClick={() => remove("contact", m.id)} className="inline-flex items-center gap-1.5 tracked text-[10px] px-4 py-2 text-destructive hover:bg-destructive hover:text-white transition-colors" style={{ border: "1px solid rgba(239,68,68,0.3)" }}>
                                                            <Trash2 size={12} /> Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* PARTNERS — CRM-style */}
                    {tab === "partners" && (
                        <div>
                            <div className="bg-white border border-coffee/10 p-4 mb-4 flex flex-wrap gap-3 items-center" data-testid="partner-toolbar">
                                <div className="relative flex-1 min-w-[220px]">
                                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-coffee/40" />
                                    <input
                                        type="text"
                                        placeholder="Search name, email, city, message…"
                                        value={partnerSearch}
                                        onChange={(e) => setPartnerSearch(e.target.value)}
                                        data-testid="partner-search"
                                        className="w-full pl-9 pr-3 py-2.5 text-sm bg-cream-light border border-coffee/15 focus:border-coffee outline-none font-body text-coffee"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Filter size={13} className="text-coffee/50" />
                                    <select value={partnerStatusFilter} onChange={(e) => setPartnerStatusFilter(e.target.value)} data-testid="partner-status-filter"
                                        className="text-[11px] tracked py-2 px-3 bg-cream-light border border-coffee/15 outline-none text-coffee">
                                        <option value="all">All status</option>
                                        {PARTNER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                    <select value={partnerTypeFilter} onChange={(e) => setPartnerTypeFilter(e.target.value)} data-testid="partner-type-filter"
                                        className="text-[11px] tracked py-2 px-3 bg-cream-light border border-coffee/15 outline-none text-coffee">
                                        <option value="all">All types</option>
                                        <option value="franchise">Franchise</option>
                                        <option value="location">Location</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <button onClick={exportPartnersCsv} className="inline-flex items-center gap-2 tracked text-[10px] px-4 py-2.5 bg-coffee text-cream hover:bg-gold hover:text-coffee transition-colors" data-testid="partner-export-csv">
                                    <Download size={12} /> Export CSV
                                </button>
                            </div>

                            <div className="bg-white border border-coffee/10 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm" data-testid="partner-table">
                                        <thead>
                                            <tr className="bg-cream/40 border-b border-coffee/10">
                                                {["Name", "Email", "City", "Type", "Investment", "Date", "Status", ""].map((h, i) => (
                                                    <th key={i} className="text-left tracked text-coffee/60 text-[10px] py-3 px-4 whitespace-nowrap">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPartners.length === 0 ? (
                                                <tr><td colSpan={8} className="py-12 text-center font-editorial text-coffee/40">No inquiries match these filters.</td></tr>
                                            ) : filteredPartners.map((p) => {
                                                const status = p.status || "new";
                                                const cls = STATUS_COLORS[status] || STATUS_COLORS.new;
                                                return (
                                                    <tr key={p.id} className="border-b border-coffee/5 hover:bg-cream/30 transition-colors" data-testid={`partner-row-${p.id}`}>
                                                        <td className="py-3 px-4 align-top">
                                                            <div className="font-editorial font-bold text-coffee">{p.name}</div>
                                                            <div className="font-mono text-[10px] text-coffee/40">{p.phone}</div>
                                                        </td>
                                                        <td className="py-3 px-4 align-top"><a href={`mailto:${p.email}`} className="text-coffee/80 gold-underline">{p.email}</a></td>
                                                        <td className="py-3 px-4 align-top text-coffee/70">{p.city || "—"}</td>
                                                        <td className="py-3 px-4 align-top"><span className="font-mono tracked text-gold text-[10px]">{p.interest || "—"}</span></td>
                                                        <td className="py-3 px-4 align-top text-coffee/80 whitespace-nowrap">{p.investment || "—"}</td>
                                                        <td className="py-3 px-4 align-top font-mono text-[10px] text-coffee/50 whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</td>
                                                        <td className="py-3 px-4 align-top">
                                                            <select
                                                                value={status}
                                                                onChange={(e) => updatePartnerStatus(p.id, e.target.value)}
                                                                data-testid={`partner-status-${p.id}`}
                                                                className={`text-[10px] tracked py-1.5 px-2 border outline-none ${cls}`}
                                                            >
                                                                {PARTNER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                                            </select>
                                                        </td>
                                                        <td className="py-3 px-4 align-top text-right">
                                                            <button onClick={() => remove("partner", p.id)} className="text-coffee/50 hover:text-destructive p-1" title="Delete">
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Expandable details */}
                            {filteredPartners.some((p) => p.message || p.source) && (
                                <div className="mt-6 space-y-2">
                                    {filteredPartners.filter((p) => p.message || p.source).slice(0, 50).map((p) => (
                                        <details key={`d-${p.id}`} className="bg-white border border-coffee/10 p-4">
                                            <summary className="cursor-pointer tracked text-[10px] text-coffee/60 hover:text-coffee">
                                                Details for <span className="text-coffee font-editorial font-medium not-italic">{p.name}</span>
                                            </summary>
                                            {p.source && <div className="mt-3 text-coffee/60 text-sm">Source: <span className="text-coffee">{p.source}</span></div>}
                                            {p.message && <p className="mt-2 font-body text-coffee/80 whitespace-pre-wrap leading-relaxed">{p.message}</p>}
                                        </details>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* CAREERS */}
                    {tab === "careers" && (
                        <div className="space-y-3">
                            {(data.careers || []).length === 0 ? <Empty /> : data.careers.map((m) => (
                                <div key={m.id} className="bg-white border border-coffee/10 p-5" data-testid={`career-${m.id}`}>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="font-editorial font-bold text-coffee text-xl">{m.name}</div>
                                            <div className="font-mono tracked text-gold text-[10px] mt-1">{m.role}</div>
                                            <a href={`mailto:${m.email}`} className="text-coffee/70 text-sm gold-underline mt-2 inline-block">{m.email}</a>
                                            <div className="text-coffee/60 text-sm mt-1">{m.phone} · {m.city}</div>
                                            <p className="font-body text-coffee/80 mt-4 whitespace-pre-wrap leading-relaxed">{m.message}</p>
                                            <div className="font-mono text-[10px] text-coffee/40 mt-3">{new Date(m.created_at).toLocaleString()}</div>
                                        </div>
                                        <button onClick={() => remove("career", m.id)} className="text-coffee/50 hover:text-destructive p-2"><Trash2 size={16} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* NEWSLETTER */}
                    {tab === "newsletter" && (
                        <div className="space-y-3">
                            {data.newsletter.length === 0 ? <Empty /> : data.newsletter.map((m) => (
                                <div key={m.email} className="bg-white border border-coffee/10 p-4 flex justify-between items-center" data-testid={`subscriber-${m.email}`}>
                                    <div>
                                        <div className="text-coffee font-body">{m.email}</div>
                                        <div className="font-mono text-[10px] text-coffee/40 mt-1">{new Date(m.created_at).toLocaleDateString()}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Empty() {
    return <div className="font-editorial font-bold text-coffee/50 text-xl">Nothing yet. Quiet morning.</div>;
}
